import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getRevenueReport, getCustomerSourceReport, getCustomerTagReport, getCustomerTrendReport } from '../services/api';

export interface ReportData {
  revenue: Array<{ month: string; total: number }>;
  sources: Array<{ source: string; count: number }>;
  tags: Array<{ tag: string; color: string; count: number }>;
  trend: Array<{ month: string; count: number; cumulative: number }>;
}

type ReportKey = 'active' | 'withArchived';

interface ReportState {
  reports: Partial<Record<ReportKey, ReportData>>;
  fetchReports: (includeArchived: boolean) => Promise<void>;
}

export const reportKey = (includeArchived: boolean): ReportKey => (includeArchived ? 'withArchived' : 'active');

/**
 * 報表資料（照 dashboardStore：persist 到 localStorage）
 * 原本用 react-query 存在記憶體，離開頁面 5 分鐘或重新整理就清空，每次回來都要重新讀取
 * 現在進頁面先顯示上次的資料，背景重抓後更新；只有第一次（沒有快取）才顯示「載入中」
 */
export const useReportStore = create<ReportState>()(
  persist(
    (set) => ({
      reports: {},
      fetchReports: async (includeArchived) => {
        try {
          const [revenue, sources, tags, trend] = await Promise.all([
            getRevenueReport(),
            getCustomerSourceReport(includeArchived),
            getCustomerTagReport(includeArchived),
            getCustomerTrendReport(includeArchived),
          ]);
          set(state => ({
            reports: {
              ...state.reports,
              [reportKey(includeArchived)]: { revenue: revenue.data, sources: sources.data, tags: tags.data, trend: trend.data },
            },
          }));
        } catch (error) {
          // 失敗時保留上次的資料
          console.error('Failed to fetch reports:', error);
        }
      },
    }),
    { name: 'report-storage' }
  )
);
